/**
 * MentionHydrationService
 *
 * TypeScript service for parsing and resolving "@" mentions in rich text content.
 * This is a typed port of the legacy mentionUtils.js functionality.
 *
 * Responsibilities:
 * - Parse reference strings into structured data
 * - Resolve references to actual data from context
 * - Process HTML for Expression mode (human-readable)
 * - Process HTML for Result mode (hydrated JSON values)
 *
 * Reference Formats (supports BOTH old and new):
 *
 * NEW FORMAT (CompositeDataPicker - square brackets):
 * - Audience entity: @audience[audienceId]
 * - Actor entity: @audience[audienceId].actors[actorId]
 * - Actor field: @audience[audienceId].actors[actorId].{fieldName}
 * - Actor array item: @audience[audienceId].actors[actorId].{fieldName}[{index}]
 * - Task entity: @task[taskId]
 * - Task field: @task[taskId].{fieldName}
 * - Task array item: @task[taskId].{fieldName}[{index}]
 *
 * OLD FORMAT (legacy - implicit IDs):
 * - Actor field: @audience{audienceId}.actors{actorId}.{fieldName}
 * - Actor array item: @audience{audienceId}.actors{actorId}.{fieldName}[{index}]
 * - Task field: @task{taskId}.{fieldName}
 * - Task array item: @task{taskId}.{fieldName}[{index}]
 * - Study case: @studyCase{studyCaseId}.{fieldName}
 */

import type {
  IMentionHydrationService,
  MentionDataContext,
  ParsedReference,
  ActorReference,
  ActorArrayItemReference,
  TaskReference,
  TaskArrayItemReference,
  StudyCaseReference,
  AudienceEntityReference,
  ActorEntityReference,
  TaskEntityReference,
  Actor,
  Audience,
} from './types';

/**
 * MentionHydrationService implementation
 */
export class MentionHydrationService implements IMentionHydrationService {
  // ===========================================================================
  // REFERENCE PARSING
  // ===========================================================================

  /**
   * Parse a reference string into structured data
   *
   * NOTE: The @ prefix is REQUIRED. Callers should normalize the reference
   * string before calling this method if needed.
   *
   * Supports BOTH formats:
   * - NEW: @audience[id].actors[id].field (CompositeDataPicker)
   * - OLD: @audienceId.actorsId.field (legacy)
   */
  parseReference(refString: string): ParsedReference | null {
    if (typeof refString !== 'string' || !refString) {
      return null;
    }

    // @ prefix is required - do NOT normalize here
    // Normalization happens in processForExpression/processForResult
    if (!refString.startsWith('@')) {
      return null;
    }

    const normalizedRef = refString;

    // =========================================================================
    // NEW FORMAT: @entity[id] with square brackets (CompositeDataPicker)
    // Order matters: most specific patterns first!
    // =========================================================================

    // NEW: Actor array item: @audience[audId].actors[actorId].{fieldName}[{idx}]
    const newActorArrayMatch = normalizedRef.match(
      /@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([a-zA-Z0-9_]+)\[(\d+)\]/
    );
    if (newActorArrayMatch && newActorArrayMatch.length === 5) {
      return {
        type: 'actor-array-item',
        audienceId: newActorArrayMatch[1],
        actorId: newActorArrayMatch[2],
        fieldName: newActorArrayMatch[3],
        arrayIndex: parseInt(newActorArrayMatch[4], 10),
      } as ActorArrayItemReference;
    }

    // NEW: Actor field: @audience[audId].actors[actorId].{fieldName}
    const newActorFieldMatch = normalizedRef.match(
      /@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([^.\[]+)/
    );
    if (newActorFieldMatch && newActorFieldMatch.length === 4) {
      return {
        type: 'actor',
        audienceId: newActorFieldMatch[1],
        actorId: newActorFieldMatch[2],
        fieldName: newActorFieldMatch[3],
      } as ActorReference;
    }

    // NEW: Actor entity (entire actor): @audience[audId].actors[actorId]
    const newActorEntityMatch = normalizedRef.match(
      /@audience\[([^\]]+)\]\.actors\[([^\]]+)\]$/
    );
    if (newActorEntityMatch && newActorEntityMatch.length === 3) {
      return {
        type: 'audience-actor',
        audienceId: newActorEntityMatch[1],
        actorId: newActorEntityMatch[2],
      } as ActorEntityReference;
    }

    // NEW: Audience entity (entire audience): @audience[audId]
    const newAudienceMatch = normalizedRef.match(/@audience\[([^\]]+)\]$/);
    if (newAudienceMatch && newAudienceMatch.length === 2) {
      return {
        type: 'audience',
        audienceId: newAudienceMatch[1],
      } as AudienceEntityReference;
    }

    // NEW: Task array item: @task[taskId].{fieldName}[{idx}]
    const newTaskArrayMatch = normalizedRef.match(
      /@task\[([^\]]+)\]\.([a-zA-Z0-9_]+)\[(\d+)\]/
    );
    if (newTaskArrayMatch && newTaskArrayMatch.length === 4) {
      return {
        type: 'task-array-item',
        taskId: newTaskArrayMatch[1],
        fieldName: newTaskArrayMatch[2],
        arrayIndex: parseInt(newTaskArrayMatch[3], 10),
      } as TaskArrayItemReference;
    }

    // NEW: Task field: @task[taskId].{fieldName}
    const newTaskFieldMatch = normalizedRef.match(/@task\[([^\]]+)\]\.([^.\[]+)/);
    if (newTaskFieldMatch && newTaskFieldMatch.length === 3) {
      return {
        type: 'task',
        taskId: newTaskFieldMatch[1],
        fieldName: newTaskFieldMatch[2],
      } as TaskReference;
    }

    // NEW: Task entity (entire task): @task[taskId]
    const newTaskEntityMatch = normalizedRef.match(/@task\[([^\]]+)\]$/);
    if (newTaskEntityMatch && newTaskEntityMatch.length === 2) {
      return {
        type: 'task-entity',
        taskId: newTaskEntityMatch[1],
      } as TaskEntityReference;
    }

    // =========================================================================
    // OLD FORMAT: @entity{id} with implicit IDs (legacy compatibility)
    // =========================================================================

    // OLD: Actor array item: @audience{audId}.actors{actorId}.{fieldName}[{idx}]
    const actorArrayMatch = normalizedRef.match(
      /@audience([^.\[]+)\.actors([^.\[]+)\.([a-zA-Z0-9_]+)\[(\d+)\]/
    );
    if (actorArrayMatch && actorArrayMatch.length === 5) {
      return {
        type: 'actor-array-item',
        audienceId: actorArrayMatch[1],
        actorId: actorArrayMatch[2],
        fieldName: actorArrayMatch[3],
        arrayIndex: parseInt(actorArrayMatch[4], 10),
      } as ActorArrayItemReference;
    }

    // OLD: Actor field: @audience{audId}.actors{actorId}.{fieldName}
    const actorMatch = normalizedRef.match(
      /@audience([^.\[]+)\.actors([^.\[]+)\.([^.\[]+)/
    );
    if (actorMatch && actorMatch.length === 4) {
      return {
        type: 'actor',
        audienceId: actorMatch[1],
        actorId: actorMatch[2],
        fieldName: actorMatch[3],
      } as ActorReference;
    }

    // OLD: Task array item: @task{taskId}.{fieldName}[{idx}]
    const taskArrayMatch = normalizedRef.match(
      /@task([^.\[]+)\.([a-zA-Z0-9_]+)\[(\d+)\]/
    );
    if (taskArrayMatch && taskArrayMatch.length === 4) {
      return {
        type: 'task-array-item',
        taskId: taskArrayMatch[1],
        fieldName: taskArrayMatch[2],
        arrayIndex: parseInt(taskArrayMatch[3], 10),
      } as TaskArrayItemReference;
    }

    // OLD: Task field: @task{taskId}.{fieldName}
    const taskMatch = normalizedRef.match(/@task([^.\[]+)\.([^.\[]+)/);
    if (taskMatch && taskMatch.length === 3) {
      return {
        type: 'task',
        taskId: taskMatch[1],
        fieldName: taskMatch[2],
      } as TaskReference;
    }

    // OLD: Study case: @studyCase{studyCaseId}.{fieldName}
    const studyCaseMatch = normalizedRef.match(/@studyCase([^.\[]+)\.([^.\[]+)/);
    if (studyCaseMatch && studyCaseMatch.length === 3) {
      return {
        type: 'study-case',
        studyCaseId: studyCaseMatch[1],
        fieldName: studyCaseMatch[2],
      } as StudyCaseReference;
    }

    return null;
  }

  // ===========================================================================
  // DATA RESOLUTION
  // ===========================================================================

  /**
   * Get raw data for a parsed reference from the data context
   */
  getRawData(parsed: ParsedReference, context: MentionDataContext): unknown {
    if (!parsed) return null;

    switch (parsed.type) {
      // Entity-only references (NEW - returns full entity)
      case 'audience':
        return this.getAudienceEntityData(parsed as AudienceEntityReference, context);
      case 'audience-actor':
        return this.getActorEntityData(parsed as ActorEntityReference, context);
      case 'task-entity':
        return this.getTaskEntityData(parsed as TaskEntityReference, context);
      // Field references (returns specific field value)
      case 'actor-array-item':
        return this.getActorArrayItemData(parsed, context);
      case 'actor':
        return this.getActorFieldData(parsed, context);
      case 'task-array-item':
        return this.getTaskArrayItemData(parsed, context);
      case 'task':
        return this.getTaskFieldData(parsed, context);
      case 'study-case':
        return this.getStudyCaseFieldData(parsed, context);
      default:
        return null;
    }
  }

  // ===========================================================================
  // ENTITY-ONLY DATA ACCESSORS (NEW)
  // ===========================================================================

  /**
   * Get full audience entity data
   */
  private getAudienceEntityData(
    parsed: AudienceEntityReference,
    context: MentionDataContext
  ): Audience | null {
    if (!Array.isArray(context.audiences)) return null;
    return context.audiences.find(aud => aud.id === parsed.audienceId) || null;
  }

  /**
   * Get full actor entity data
   */
  private getActorEntityData(
    parsed: ActorEntityReference,
    context: MentionDataContext
  ): Actor | null {
    return this.findActor(parsed.audienceId, parsed.actorId, context);
  }

  /**
   * Get full task entity data
   */
  private getTaskEntityData(
    parsed: TaskEntityReference,
    context: MentionDataContext
  ): unknown {
    return this.findTask(parsed.taskId, context);
  }

  // ===========================================================================
  // FIELD DATA ACCESSORS
  // ===========================================================================

  /**
   * Get actor array item data
   */
  private getActorArrayItemData(
    parsed: ActorArrayItemReference,
    context: MentionDataContext
  ): unknown {
    const actor = this.findActor(parsed.audienceId, parsed.actorId, context);
    if (!actor) return null;

    const arr = actor[parsed.fieldName];
    if (!Array.isArray(arr) || arr.length <= parsed.arrayIndex) {
      return null;
    }

    return arr[parsed.arrayIndex];
  }

  /**
   * Get actor field data
   */
  private getActorFieldData(
    parsed: ActorReference,
    context: MentionDataContext
  ): unknown {
    const actor = this.findActor(parsed.audienceId, parsed.actorId, context);
    if (!actor) return null;

    return actor[parsed.fieldName];
  }

  /**
   * Get task array item data
   */
  private getTaskArrayItemData(
    parsed: TaskArrayItemReference,
    context: MentionDataContext
  ): unknown {
    const task = this.findTask(parsed.taskId, context);
    if (!task) return null;

    const arr = task[parsed.fieldName];
    if (!Array.isArray(arr) || arr.length <= parsed.arrayIndex) {
      return null;
    }

    return arr[parsed.arrayIndex];
  }

  /**
   * Get task field data
   */
  private getTaskFieldData(
    parsed: TaskReference,
    context: MentionDataContext
  ): unknown {
    const task = this.findTask(parsed.taskId, context);
    if (!task) return null;

    return task[parsed.fieldName];
  }

  /**
   * Get study case field data
   */
  private getStudyCaseFieldData(
    parsed: StudyCaseReference,
    context: MentionDataContext
  ): unknown {
    if (!Array.isArray(context.studyCases)) return null;

    const studyCase = context.studyCases.find(sc => sc.id === parsed.studyCaseId);
    if (!studyCase) return null;

    return studyCase[parsed.fieldName];
  }

  /**
   * Find actor by audience and actor ID
   */
  private findActor(
    audienceId: string,
    actorId: string,
    context: MentionDataContext
  ): Actor | null {
    const audience = Array.isArray(context.audiences)
      ? context.audiences.find(aud => aud.id === audienceId)
      : null;
    if (!audience) return null;

    const actors = context.actorsByAudience?.[audienceId];
    if (!Array.isArray(actors)) return null;

    return actors.find(act => act.id === actorId) || null;
  }

  /**
   * Find task by ID
   */
  private findTask(taskId: string, context: MentionDataContext) {
    if (!Array.isArray(context.tasks)) return null;
    return context.tasks.find(t => t.id === taskId) || null;
  }

  // ===========================================================================
  // EXPRESSION MODE PROCESSING
  // ===========================================================================

  /**
   * Process HTML with mentions for Expression mode (human-readable)
   */
  processForExpression(html: string, context: MentionDataContext): string {
    if (!html) return '';

    // Use DOMParser for safe HTML manipulation
    const doc = this.parseHTML(html);
    const spans = doc.querySelectorAll('span[data-mention-id]');

    spans.forEach(span => {
      const mentionId = span.getAttribute('data-mention-id');
      if (!mentionId) return;

      const fullMentionId = mentionId.startsWith('@') ? mentionId : `@${mentionId}`;
      const parsed = this.parseReference(fullMentionId);

      let displayText = '';

      if (parsed) {
        const rawData = this.getRawData(parsed, context);
        displayText = this.extractDisplayText(rawData);
      }

      // Fallback to formatted reference if no display text
      if (!displayText) {
        displayText = this.formatReferenceForDisplay(fullMentionId, context);
      }

      span.textContent = displayText;
      span.setAttribute('data-label', displayText);
    });

    return this.serializeHTML(doc);
  }

  /**
   * Extract display text from raw data
   */
  private extractDisplayText(rawData: unknown): string {
    if (rawData === null || rawData === undefined) {
      return '';
    }

    // Array of objects with description - extract descriptions
    if (
      Array.isArray(rawData) &&
      rawData.length > 0 &&
      rawData.every(item => item && typeof item === 'object' && 'description' in item)
    ) {
      return rawData
        .map(item => (item as { description: string }).description)
        .filter(Boolean)
        .join(', ');
    }

    // Object with description field
    if (
      rawData &&
      typeof rawData === 'object' &&
      !Array.isArray(rawData) &&
      'description' in rawData
    ) {
      return String((rawData as { description: string }).description);
    }

    // Object with text field
    if (
      rawData &&
      typeof rawData === 'object' &&
      !Array.isArray(rawData) &&
      'text' in rawData
    ) {
      return String((rawData as { text: string }).text);
    }

    // Primitive value
    return String(rawData);
  }

  // ===========================================================================
  // RESULT MODE PROCESSING
  // ===========================================================================

  /**
   * Process HTML with mentions for Result mode (hydrated JSON values)
   */
  processForResult(html: string, context: MentionDataContext): string {
    if (!html) return '';

    const doc = this.parseHTML(html);
    const spans = doc.querySelectorAll('span[data-mention-id]');

    spans.forEach(span => {
      const mentionId = span.getAttribute('data-mention-id');
      if (!mentionId) {
        this.replaceWithText(span, '[Invalid mention]');
        return;
      }

      const fullMentionId = mentionId.startsWith('@') ? mentionId : `@${mentionId}`;
      const parsed = this.parseReference(fullMentionId);

      if (!parsed) {
        this.replaceWithText(span, `[Cannot parse: ${fullMentionId}]`);
        return;
      }

      const rawData = this.getRawData(parsed, context);
      let resolved = '';

      if (rawData !== null && rawData !== undefined) {
        const cleaned = this.deepOmitCreatedAt(rawData);
        if (typeof cleaned === 'object') {
          resolved = JSON.stringify(cleaned, null, 2);
        } else {
          resolved = String(cleaned);
        }
      } else {
        const displayRef = this.formatReferenceForDisplay(fullMentionId, context);
        resolved = `[Not found: ${displayRef}]`;
      }

      this.replaceWithText(span, resolved);
    });

    // Return plain text content (not HTML)
    return doc.body.textContent || '';
  }

  /**
   * Deep remove createdAt and created_at fields from any object/array
   */
  private deepOmitCreatedAt(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(item => this.deepOmitCreatedAt(item));
    }

    if (value && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      for (const key in value as Record<string, unknown>) {
        if (
          Object.prototype.hasOwnProperty.call(value, key) &&
          key !== 'createdAt' &&
          key !== 'created_at'
        ) {
          result[key] = this.deepOmitCreatedAt((value as Record<string, unknown>)[key]);
        }
      }
      return result;
    }

    return value;
  }

  // ===========================================================================
  // DISPLAY FORMATTING
  // ===========================================================================

  /**
   * Format a reference string for display
   */
  formatReferenceForDisplay(refString: string, context: MentionDataContext): string {
    const parsed = this.parseReference(refString);
    if (!parsed) return refString;

    switch (parsed.type) {
      // Entity-only references (NEW)
      case 'audience': {
        const audience = context.audiences?.find(
          aud => aud.id === (parsed as AudienceEntityReference).audienceId
        );
        if (!audience) return refString;
        return `@${audience.name || audience.id}`;
      }

      case 'audience-actor': {
        const actorRef = parsed as ActorEntityReference;
        const audience = context.audiences?.find(aud => aud.id === actorRef.audienceId);
        const actors = context.actorsByAudience?.[actorRef.audienceId] || [];
        const actor = actors.find(act => act.id === actorRef.actorId);

        if (!audience || !actor) return refString;

        const audienceName = audience.name || audience.id;
        const actorName = actor.name || actor.id;
        return `@${audienceName}.${actorName}`;
      }

      case 'task-entity': {
        const task = context.tasks?.find(
          t => t.id === (parsed as TaskEntityReference).taskId
        );
        if (!task) return refString;
        return `@${task.name || task.id}`;
      }

      // Field references
      case 'actor':
      case 'actor-array-item': {
        const audience = context.audiences?.find(
          aud => aud.id === (parsed as ActorReference | ActorArrayItemReference).audienceId
        );
        const actors =
          context.actorsByAudience?.[
            (parsed as ActorReference | ActorArrayItemReference).audienceId
          ] || [];
        const actor = actors.find(
          act => act.id === (parsed as ActorReference | ActorArrayItemReference).actorId
        );

        if (!audience || !actor) return refString;

        const audienceName = audience.name || audience.id;
        const actorName = actor.name || actor.id;

        if (parsed.type === 'actor-array-item') {
          return `@${audienceName}.${actorName}.${parsed.fieldName}[${parsed.arrayIndex}]`;
        }
        return `@${audienceName}.${actorName}.${parsed.fieldName}`;
      }

      case 'task':
      case 'task-array-item': {
        const task = context.tasks?.find(
          t => t.id === (parsed as TaskReference | TaskArrayItemReference).taskId
        );
        if (!task) return refString;

        const taskName = task.name || task.id;

        if (parsed.type === 'task-array-item') {
          return `@${taskName}.${parsed.fieldName}[${parsed.arrayIndex}]`;
        }
        return `@${taskName}.${parsed.fieldName}`;
      }

      case 'study-case': {
        const studyCase = context.studyCases?.find(
          sc => sc.id === (parsed as StudyCaseReference).studyCaseId
        );
        if (!studyCase) return refString;

        const scName = studyCase.featureName || studyCase.id;
        return `@${scName}.${parsed.fieldName}`;
      }

      default:
        return refString;
    }
  }

  // ===========================================================================
  // HTML HELPERS
  // ===========================================================================

  /**
   * Parse HTML string into DOM document
   */
  private parseHTML(html: string): Document {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      return parser.parseFromString(`<body>${html}</body>`, 'text/html');
    }

    // Fallback for non-browser environments (tests)
    // Create a simple document-like structure
    const doc = {
      body: {
        innerHTML: html,
        textContent: '',
      },
      querySelectorAll: (selector: string) => {
        // Simple regex-based extraction for testing
        if (selector === 'span[data-mention-id]') {
          const matches: { getAttribute: (attr: string) => string | null; textContent: string; setAttribute: (attr: string, value: string) => void }[] = [];
          const regex = /<span[^>]*data-mention-id="([^"]*)"[^>]*>([^<]*)<\/span>/g;
          let match;
          while ((match = regex.exec(html)) !== null) {
            const mentionId = match[1];
            matches.push({
              getAttribute: (attr: string) => attr === 'data-mention-id' ? mentionId : null,
              textContent: match[2],
              setAttribute: () => {},
            });
          }
          return matches;
        }
        return [];
      },
    };

    return doc as unknown as Document;
  }

  /**
   * Serialize DOM back to HTML string
   */
  private serializeHTML(doc: Document): string {
    return doc.body.innerHTML;
  }

  /**
   * Replace element with text node
   */
  private replaceWithText(element: Element, text: string): void {
    if (element.replaceWith) {
      element.replaceWith(document.createTextNode(text));
    } else {
      // Fallback for non-browser environments
      (element as unknown as { textContent: string }).textContent = text;
    }
  }
}

/**
 * Singleton instance for convenience
 */
export const mentionHydrationService = new MentionHydrationService();
